import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { MenuItem, MessageService, ConfirmationService } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { CrmClient, CrmDuplicatePair, CrmRoleOption, PageResponse, crmRoleLabel } from '../crm.model';
import { CrmService } from '../crm.service';
import { ToolbarService } from '../../services/toolbar.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';

@Component({
  selector: 'app-clients-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    MultiSelectModule,
    SelectModule,
    TooltipModule,
    RippleModule,
    BreadcrumbModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './clients-list.component.html',
  styleUrls: ['./clients-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientsListComponent implements OnInit {
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  clients = signal<CrmClient[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  first = signal(0);
  rows = signal(10);
  searchText = signal('');
  categoryFilter = signal('');
  businessTypeFilter = signal('');
  sourceFilter = signal('');
  approvalStatusFilter = signal('');
  roleFilters = signal<string[]>([]);
  columnFilters = signal<Record<string, string>>({});
  lastUpdated = signal<Date | null>(null);
  duplicateDialog = signal(false);
  duplicates = signal<CrmDuplicatePair<CrmClient>[]>([]);
  scorecards = signal<Record<string, any>>({});
  crmRoles = signal<CrmRoleOption[]>([]);

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/clients' },
    { label: 'Clients', routerLink: '/crm/clients' }
  ];

  metrics = computed<PageMetric[]>(() => [
    { label: 'Total clients', value: Number(this.scorecards()['total'] || this.totalRecords()) },
    { label: 'Awaiting finance', value: Number(this.scorecards()['draft'] || 0), tone: 'warn' },
    { label: 'Approved', value: Number(this.scorecards()['approved'] || 0), tone: 'success' },
    { label: 'With compliance', value: Number(this.scorecards()['withCompliance'] || 0), tone: 'info' }
  ]);

  categories = [
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Premium', value: 'PREMIUM' },
    { label: 'VIP', value: 'VIP' },
    { label: 'Partner', value: 'PARTNER' }
  ];

  businessTypes = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' }
  ];

  approvalStatuses = [
    { label: 'Draft – Awaiting Finance', value: 'DRAFT' },
    { label: 'Approved', value: 'APPROVED' }
  ];

  ngOnInit() {
    this.loadRoles();
    this.loadScorecards();
    this.loadClients();
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadScorecards() {
    this.crmService.getClientScorecards(this.toolbarService.customer()?.company?.id).subscribe(cards => this.scorecards.set(cards || {}));
  }

  loadClients(event?: TableLazyLoadEvent) {
    if (event) {
      this.first.set(event.first ?? 0);
      this.rows.set(event.rows ?? 10);
    }
    const page = Math.floor(this.first() / this.rows());
    this.loading.set(true);
    this.crmService.searchClients({
      companyId: this.toolbarService.customer()?.company?.id,
      searchText: this.searchText(),
      category: this.categoryFilter(),
      businessType: this.businessTypeFilter(),
      source: this.sourceFilter(),
      clientNumber: this.columnFilters()['clientNumber'] || '',
      companyName: this.columnFilters()['companyName'] || '',
      website: this.columnFilters()['website'] || '',
      primaryEmail: this.columnFilters()['primaryEmail'] || '',
      phoneNumber: this.columnFilters()['phoneNumber'] || '',
      approvalStatus: this.approvalStatusFilter(),
      roles: this.roleFilters(),
      page,
      size: this.rows(),
      sortBy: 'createdAt',
      sortDir: 'desc'
    }).pipe(finalize(() => this.loading.set(false))).subscribe((response: PageResponse<CrmClient>) => {
      this.clients.set(response.content || []);
      this.totalRecords.set(response.totalElements || 0);
      this.lastUpdated.set(new Date());
      this.loadScorecards();
    });
  }

  applySearch() {
    this.first.set(0);
    this.loadClients();
  }

  clearSearch() {
    this.searchText.set('');
    this.categoryFilter.set('');
    this.businessTypeFilter.set('');
    this.sourceFilter.set('');
    this.approvalStatusFilter.set('');
    this.roleFilters.set([]);
    this.columnFilters.set({});
    this.first.set(0);
    this.loadClients();
  }

  openNew() {
    this.router.navigate(['/crm/clients/new']);
  }

  editClient(client: CrmClient) {
    if (client.id) this.router.navigate(['/crm/clients', client.id, 'edit']);
  }

  deleteClient(client: CrmClient) {
    if (!client.id) return;
    this.confirmationService.confirm({
      header: 'Confirm Deletion',
      message: `Delete client: <b>${client.companyName}</b>?`,
      accept: () => this.crmService.deleteClient(client.id!).subscribe(() => {
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Client deleted' });
        this.loadClients();
      })
    });
  }

  loadDuplicates() {
    this.crmService.getClientDuplicates(this.toolbarService.customer()?.company?.id).subscribe(response => {
      this.duplicates.set(response.data?.pairs || []);
      this.duplicateDialog.set(true);
    });
  }

  mergeDuplicate(pair: CrmDuplicatePair<CrmClient>) {
    if (!pair.primary.id || !pair.duplicate.id) return;
    this.confirmationService.confirm({
      header: 'Merge duplicate client',
      message: `Merge <b>${pair.duplicate.companyName}</b> into <b>${pair.primary.companyName}</b>?`,
      accept: () => this.crmService.mergeClient(pair.primary.id!, pair.duplicate.id!, this.toolbarService.customer()?.company?.id).subscribe(response => {
        if (response.success) {
          this.messageService.add({ severity: 'success', summary: 'Merged', detail: 'Client duplicates merged successfully' });
          this.duplicateDialog.set(false);
          this.loadClients();
        }
      })
    });
  }

  setColumnFilter(field: string, value: string) {
    this.columnFilters.update(filters => ({ ...filters, [field]: value || '' }));
  }

  getCategorySeverity(category: string) {
    switch (category) {
      case 'VIP': return 'success';
      case 'PREMIUM': return 'warn';
      case 'PARTNER': return 'info';
      default: return 'secondary';
    }
  }

  approvalSeverity(status?: string | null): 'success' | 'warn' {
    return status === 'APPROVED' ? 'success' : 'warn';
  }

  roleLabel(role?: string | null): string {
    return crmRoleLabel(role);
  }

  get activeSearchCount(): number {
    const filters = this.columnFilters();
    return [
      this.searchText(),
      this.categoryFilter(),
      this.businessTypeFilter(),
      this.sourceFilter(),
      this.approvalStatusFilter(),
      this.roleFilters().length ? this.roleFilters().join(',') : '',
      filters['clientNumber'],
      filters['companyName'],
      filters['website'],
      filters['primaryEmail'],
      filters['phoneNumber']
    ].filter(value => !!String(value || '').trim()).length;
  }

  get b2bClientCount(): number {
    return this.clients().filter(client => client.businessType === 'B2B').length;
  }

  get linkedClientCount(): number {
    return this.clients().filter(client => client.linkedCompanyId || client.linkedCustomerId).length;
  }

  get documentReadyCount(): number {
    return this.clients().filter(client => client.taxRegNumber || client.crNumber).length;
  }

  get lastUpdatedText(): string {
    const updated = this.lastUpdated();
    return updated ? `Updated ${updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
  }
}
